import { ref, computed, watch } from 'vue'
import { 
  createSearchIndex, 
  searchToys, 
  getSearchSuggestions,
  filterByCategory,
  filterByDifficulty
} from '@/utils/search'

/**
 * 搜索组合函数
 * 封装搜索逻辑和状态管理
 */
export function useSearch(toys) {
  // 状态
  const searchQuery = ref('')
  const searchResults = ref([])
  const suggestions = ref([])
  const isSearching = ref(false)
  const selectedCategory = ref('')
  const selectedDifficulty = ref('')
  
  // 搜索索引（缓存）
  let searchIndex = null
  
  // 搜索结果缓存
  const resultsCache = new Map()
  const MAX_CACHE_SIZE = 50

  /**
   * 初始化搜索索引
   * 每次调用时重新创建索引，确保使用最新的数据
   */
  function initializeSearchIndex(toysList) {
    const toysToIndex = toysList || toys.value || toys
    if (toysToIndex && toysToIndex.length > 0) {
      // 每次都重新创建索引，确保使用最新数据
      searchIndex = createSearchIndex(toysToIndex)
    }
  }

  /**
   * 监听toys变化，当数据更新时重新创建索引
   */
  watch(() => toys.value, (newToys) => {
    if (newToys && newToys.length > 0) {
      // 检查数据是否完整（至少有一个toy有titleEn或keywords）
      const hasCompleteData = newToys.some(toy => toy.titleEn || (Array.isArray(toy.keywords) && toy.keywords.length > 0))
      if (hasCompleteData) {
        console.log('检测到toys数据更新，重新创建搜索索引')
        initializeSearchIndex(newToys)
      }
    }
  }, { deep: true })

  /**
   * 执行搜索
   */
  function performSearch(query) {
    if (!query || query.trim().length === 0) {
      searchResults.value = []
      return
    }

    // 将查询转换为小写，确保大小写不敏感（与searchToys中的处理一致）
    const trimmedQuery = query.trim().toLowerCase()
    
    // 检查缓存（使用小写查询作为缓存键的一部分）
    const cacheKey = `${trimmedQuery}-${selectedCategory.value}-${selectedDifficulty.value}`
    if (resultsCache.has(cacheKey)) {
      searchResults.value = resultsCache.get(cacheKey)
      return
    }

    isSearching.value = true

    try {
      // 初始化索引（每次都重新创建以确保使用最新数据）
      const toysToSearch = toys.value || toys
      console.log('执行搜索，toy数量:', toysToSearch?.length || 0)
      initializeSearchIndex(toysToSearch)

      if (!searchIndex) {
        console.warn('搜索索引未创建')
        searchResults.value = []
        return
      }

      // 执行搜索（searchToys内部会再次转换为小写，但这里已经转换了，所以是安全的）
      let results = searchToys(searchIndex, trimmedQuery)
      console.log('搜索结果数量:', results.length)

      // 应用分类过滤
      if (selectedCategory.value) {
        results = filterByCategory(results, selectedCategory.value)
      }

      // 应用难度过滤
      if (selectedDifficulty.value) {
        results = filterByDifficulty(results, selectedDifficulty.value)
      }

      searchResults.value = results

      // 缓存结果
      addToCache(cacheKey, results)
    } catch (error) {
      console.error('Search error:', error)
      searchResults.value = []
    } finally {
      isSearching.value = false
    }
  }

  /**
   * 获取搜索建议
   */
  function updateSuggestions(query) {
    if (!query || query.trim().length < 2) {
      suggestions.value = []
      return
    }

    try {
      // 初始化索引
      initializeSearchIndex(toys.value || toys)

      if (!searchIndex) {
        suggestions.value = []
        return
      }

      // 将查询转换为小写，确保大小写不敏感
      const lowerQuery = query.trim().toLowerCase()
      suggestions.value = getSearchSuggestions(searchIndex, lowerQuery, 5)
    } catch (error) {
      console.error('Suggestions error:', error)
      suggestions.value = []
    }
  }

  /**
   * 添加到缓存
   */
  function addToCache(key, value) {
    // 限制缓存大小
    if (resultsCache.size >= MAX_CACHE_SIZE) {
      const firstKey = resultsCache.keys().next().value
      resultsCache.delete(firstKey)
    }
    
    resultsCache.set(key, value)
  }

  /**
   * 清除缓存
   */
  function clearCache() {
    resultsCache.clear()
  }

  /**
   * 高亮搜索关键词
   */
  function highlightMatches(text, matches) {
    if (!matches || matches.length === 0) {
      return text
    }

    // 按位置排序匹配项
    const sortedMatches = [...matches].sort((a, b) => a.indices[0][0] - b.indices[0][0])
    
    let result = ''
    let lastIndex = 0

    sortedMatches.forEach(match => {
      match.indices.forEach(([start, end]) => {
        // 添加未匹配的文本
        result += text.substring(lastIndex, start)
        
        // 添加高亮的匹配文本
        result += `<mark class="search-highlight">${text.substring(start, end + 1)}</mark>`
        
        lastIndex = end + 1
      })
    })

    // 添加剩余文本
    result += text.substring(lastIndex)

    return result
  }

  /**
   * 获取高亮后的结果
   */
  const highlightedResults = computed(() => {
    return searchResults.value.map(result => {
      const titleMatches = result.matches.filter(m => m.key === 'title')
      const titleEnMatches = result.matches.filter(m => m.key === 'titleEn')
      const descriptionMatches = result.matches.filter(m => m.key === 'description')
      const conceptMatches = result.matches.filter(m => m.key === 'conceptSearchable')

      return {
        ...result,
        highlightedTitle: titleMatches.length > 0 
          ? highlightMatches(result.toy.title, titleMatches)
          : result.toy.title,
        highlightedTitleEn: titleEnMatches.length > 0 && result.toy.titleEn
          ? highlightMatches(result.toy.titleEn, titleEnMatches)
          : result.toy.titleEn || '',
        highlightedDescription: descriptionMatches.length > 0
          ? highlightMatches(result.toy.description, descriptionMatches)
          : result.toy.description,
        // 如果concept字段有匹配，标记为true（用于在搜索结果中显示提示）
        hasConceptMatch: conceptMatches.length > 0
      }
    })
  })

  /**
   * 搜索结果数量
   */
  const resultCount = computed(() => searchResults.value.length)

  /**
   * 是否有搜索结果
   */
  const hasResults = computed(() => searchResults.value.length > 0)

  /**
   * 监听过滤条件变化，重新搜索
   */
  watch([selectedCategory, selectedDifficulty], () => {
    if (searchQuery.value) {
      performSearch(searchQuery.value)
    }
  })

  return {
    // 状态
    searchQuery,
    searchResults,
    suggestions,
    isSearching,
    selectedCategory,
    selectedDifficulty,
    
    // 计算属性
    highlightedResults,
    resultCount,
    hasResults,
    
    // 方法
    performSearch,
    updateSuggestions,
    initializeSearchIndex,
    clearCache
  }
}
